import { getResellersData } from '../../modules/reseller/action'
import { getCurrentResaleAuthorizationInCreation } from '../../modules/resaleAuthorization/action'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { useEffect, useMemo, ReactNode } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from '../../store'
import { setPageNumber, setPageSize } from '../../modules/privateOffer/action'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
  useScreenSize,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { makeStyles } from 'tss-react/mui'
import {
  ResaleAuthorizationsState,
  ResellerInfo,
} from '../../admin/modules/resaleAuthorizations/reducer'
import {
  getCurrentResaleAuthorization,
  getResellerAgreementData,
} from '../../admin/modules/resaleAuthorizations/actions'
export const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    backgroundColor: theme.palette.opaque.light,
  },
}))

const defaultPageNumber = 1

type ResaleAuthorizationsWrapperProps = {
  secureKeyProp?: string
  partnerIdProp?: string
  agreementIdProp?: string
  productIdProp?: string
  children:
    | ReactNode
    | React.FC<{
        externalBaseURL?: string
        search?: string
        crmOpportunityId?: string
        crmObjectLink?: string
      }>
}

const ResaleAuthorizationsWrapper: React.FC<
  ResaleAuthorizationsWrapperProps
> = ({
  children,
  secureKeyProp,
  partnerIdProp,
  agreementIdProp,
  productIdProp,
}: ResaleAuthorizationsWrapperProps) => {
  const { search, pathname } = useLocation()
  const { classes } = useStyles()
  const dispatch = useDispatch()

  const {
    secureKey: secureKeyParam,
    partnerId: partnerIdParam,
    resaleAuthorizationId: agreementIdParam,
    productId: productIdParam,
    cloud: routeCloud,
    accountId: routeAccountId,
  } = useParams<{
    secureKey: string
    partnerId: string
    resaleAuthorizationId?: string
    productId?: string
    cloud?: string
    accountId?: string
  }>()
  const secureKey = secureKeyParam || secureKeyProp
  const partnerId = partnerIdParam || partnerIdProp
  const agreementId = agreementIdParam || agreementIdProp
  const productId = productIdParam || productIdProp

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const createPrivateOfferModalOpen = useSelector<boolean>(
    state => state.productListingFilters?.createPrivateOfferModalOpen ?? false
  )

  const selectedCloudMarketplace = useCurrentCloudProvider()

  const { resellerAgreements } = useSelector<ResaleAuthorizationsState>(
    state => state.cppoReseller
  )

  const crmOpportunityId = new URLSearchParams(search)
    .get('hs_object_id')
    ?.toLowerCase()

  const resellers = useSelector<ResellerInfo[]>(
    state => state.resellers.resellers
  )
  const resellersLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.RESELLERS_DATA)
  )

  const crmObjectLink = new URLSearchParams(search).get('object_link') || ''
  const hasAzureHubspotTemplate =
    pathname.includes('/external-apps/hubspot/') &&
    routeCloud?.toUpperCase() === 'AZURE' &&
    Boolean(new URLSearchParams(search).get('templateId'))
  const {
    isResaleListPage,
    isNewAgreementPage,
    isEditAgreementPage,
    isAgreementDetailsPage,
  } = useMemo(
    () => ({
      isResaleListPage:
        (pathname.includes('private-offers') ||
          pathname.includes('resale-authorizations')) &&
        !agreementId,
      isNewAgreementPage:
        pathname.includes('resale-authorizations/edit') && !agreementId,
      isEditAgreementPage:
        pathname.includes('resale-authorizations') &&
        pathname.includes('edit') &&
        agreementId,
      isAgreementDetailsPage:
        pathname.includes('resale-authorizations') &&
        pathname.includes('details') &&
        agreementId,
    }),
    [pathname, agreementId]
  )

  const externalBaseURL = useMemo(() => {
    const base =
      routeCloud && routeAccountId
        ? `/external-apps/hubspot/v1/${secureKey}/partners/${partnerId}/cloud/${routeCloud}/accounts/${routeAccountId}/product-listing`
        : `/external-apps/hubspot/v1/${secureKey}/partners/${partnerId}/product-listing`
    return `${base}${productId ? `/${productId}` : ''}`
  }, [secureKey, partnerId, productId, routeCloud, routeAccountId])
  const { products, selectedProduct: selectedProductFromFilter } =
    useFlyOutProductsBasedOnSelectedCloud()

  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const { pageSize: pageSizeValue } = useScreenSize()

  const pageSizeTobeSent = pageSizeValue

  const fetchPageData = async () => {
    if (partnerId && secureKey && routeCloud && routeAccountId) {
      if (
        isResaleListPage &&
        selectedProductFromFilter !== undefined &&
        !isEmpty(products)
      ) {
        await dispatch(
          getResellerAgreementData({
            partnerId: routeAccountId,
            productId: selectedProductFromFilter,
            crmOpportunityId,
            pageSize: pageSizeTobeSent,
            cloud: selectedCloudTypeProductListing,
          })
        )
      } else if ((isNewAgreementPage || isEditAgreementPage) && productId) {
        if (
          routeCloud?.toUpperCase() !== 'AZURE' &&
          isEmpty(resellers) &&
          !resellersLoading
        ) {
          await dispatch(getResellersData(routeAccountId))
        }
        if (!isNewAgreementPage || !hasAzureHubspotTemplate) {
          await dispatch(
            getCurrentResaleAuthorizationInCreation({
              partnerId: routeAccountId,
              productId: productId || '',
              resaleAuthorizationId: agreementId || '',
              crmOpportunityId,
              cloud: selectedCloudTypeProductListing,
            })
          )
        }
      } else if (isAgreementDetailsPage && agreementId) {
        await dispatch(
          getCurrentResaleAuthorization({
            partnerId: routeAccountId,
            resaleAuthorizationId: agreementId,
            crmOpportunityId,
            productId,
            cloud: selectedCloudMarketplace,
          })
        )
      }

      dispatch(setPageNumber(defaultPageNumber))
      dispatch(setPageSize(pageSizeTobeSent))
    }
  }

  useEffect(() => {
    fetchPageData()
  }, [
    secureKey,
    partnerId,
    agreementId,
    selectedProductFromFilter,
    selectedCloudTypeProductListing,
  ])

  if (
    !createPrivateOfferModalOpen &&
    loading &&
    isResaleListPage &&
    isEmpty(resellerAgreements) &&
    !(isNewAgreementPage || isEditAgreementPage)
  ) {
    return <div data-testid="resale-authorizations-loader" />
  }

  return (
    <div data-testid="resale-authorizations-wrapper">
      {typeof children === 'function'
        ? children({ externalBaseURL, search, crmOpportunityId, crmObjectLink })
        : children}
    </div>
  )
}

export default ResaleAuthorizationsWrapper
